import React, { useState, useEffect, useContext, useRef } from 'react';
import './StaffCheckIn.css';
import Modal from '../Modal/Modal';
import SearchSelect from '../SearchSelect/SearchSelect';
import MoodSelection from '../MoodSelection/MoodSelection';
import Timer from '../Timer/Timer';
import TextAreaInput from '../TextAreaInput/TextAreaInput';
import ActionButtons from '../ActionButtons/ActionButtons';
import baseApi from '../../common/api/base';
import DefaultMonitoredIndividual from '../DefaultMonitoredIndividualIcon/DefaultMonitoredIndividualIcon';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import { CheckinContext } from '../../contexts/CheckinContext';
import logComponent from '../../common/logComponent';
import lowerCaseFirstLetter from '../../common/lowerCaseFirstLetter';
import MonitoredIndividualImage from '../MonitoredIndividualImage/MonitoredIndividualImage';

export default function StaffCheckIn({ close }) {
  const environmentContext = useContext(EnvironmentContext);
  const checkinContext = useContext(CheckinContext);
  const [status, setStatus] = useState('initial');
  const [modalShouldExit, setModalShouldExit] = useState(false);
  const [startTime, setStartTime] = useState(Date.now());
  const [formIsInvalid, setFormIsInvalid] = useState(false);
  const [staffCheckInInstance, setStaffCheckInInstance] = useState({
    staffCheckHistoryReasonTypeTags: null,
    checkInOn: {
      value: new Date(),
      valid: true
    },
    checkOutOn: null,
    observedMood: null,
    staffNote: null,
    familyNote: null
  });
  const staffCheckInRef = useRef({
    staffCheckHistoryReasonTypeTags: null,
    checkInOn: {
      value: new Date(),
      valid: true
    },
    checkOutOn: null,
    observedMood: null,
    staffNote: null,
    familyNote: null
  });

  const setStaffCheckInRef = (el) => {
    if (logComponent('StaffCheckIn')) { console.debug('StaffCheckIn setStaffCheckInRef'); }
    staffCheckInRef.current = el;
  }
  const [cancellationAttempts, setCancellationAttempts] = useState(0);
  const { activeUser: user, organization, selectedMonitoredIndividual, selectedUnit } = environmentContext;

  const actionButtons = useRef(null);
  const modalRef = useRef(null);

  if (logComponent('StaffCheckIn')) { console.debug('StaffCheckIn rendered'); }

  const setModalRef = (el) => {
    if (logComponent('StaffCheckIn')) { console.debug('StaffCheckIn setModalRef'); }
    modalRef.current = el;
  }  

  useEffect(() => {
    if (logComponent('StaffCheckIn')) { console.debug('StaffCheckIn onMount'); }
    checkinContext.startCheckin();

    window.addEventListener('beforeunload', silentCancelCheckin);
    return () => {
      window.removeEventListener('beforeunload', silentCancelCheckin);
      checkinContext.endCheckin();

    };

  }, [])

  const validateField = (field) => {
    if (logComponent('StaffCheckIn')) { console.debug('StaffCheckIn validateField'); }
    if (field.name === 'StaffCheckHistoryReasonTypeTags') {
      sessionStorage.setItem(field.name, field.value.map(tag => tag.staffCheckReasonTypeId).join(','));
    } else {
      sessionStorage.setItem(field.name, field.value);
    }
    let newStaffCheckIn = { ...staffCheckInRef.current };

    newStaffCheckIn[lowerCaseFirstLetter(field.name)] = field;

    setStaffCheckInRef(newStaffCheckIn);
    setStaffCheckInInstance(newStaffCheckIn);
  }  

  const submitCheckIn = () => {
    if (logComponent('StaffCheckIn')) { console.debug('StaffCheckIn submitCheckIn'); }
    const requiredFields = getRequiredFieldList();

    let isValid = requiredFields.reduce((prev, curr) => prev && staffCheckInInstance[curr] && staffCheckInInstance[curr].isValid, true);

    if (!isValid) {
      handleInvalidSubmission();
      return;
    }

    setFormIsInvalid(false);

    let staffCheckInEntries = Object.entries(staffCheckInInstance);
    let staffCheckInThis = {};

    staffCheckInEntries.forEach(entry => {
      staffCheckInThis[entry[0]] = entry[1] ? entry[1].value : null;
    })

    // Move StaffNote and FamilyNote into StaffCheckHistoryNote object array
    staffCheckInThis.staffCheckHistoryNotes = [];

    if (staffCheckInThis.staffNote) {
      staffCheckInThis.staffCheckHistoryNotes.push({ body: staffCheckInThis.staffNote, type: 'Staff'});
    }

    if (staffCheckInThis.familyNote) {
      staffCheckInThis.staffCheckHistoryNotes.push({ body: staffCheckInThis.familyNote, type: 'Family'});
    }

    // Delete original StaffNote and FamilyNote from StaffCheckIn object
    delete staffCheckInThis.staffNote;
    delete staffCheckInThis.familyNote;

    // Add check out time, user id, monitored individual id, unit id
    staffCheckInThis.checkOutOn = new Date();
    staffCheckInThis.userId = user.userId;

    if (selectedMonitoredIndividual) {
      staffCheckInThis.monitoredIndividualId = selectedMonitoredIndividual.monitoredIndividualId;
      staffCheckInThis.unitId = selectedMonitoredIndividual.monitoredIndividualUnits.length > 0 ? selectedMonitoredIndividual.monitoredIndividualUnits[0].unit.unitId : null;  
    } else if (selectedUnit) {
      staffCheckInThis.unitId = selectedUnit.unitId;
    }

    setStatus('pending');
    
    baseApi.CheckIn.Save(staffCheckInThis)
      .then(
        result => {
          setStatus('success');
          startModalExit();
        },
        error => {
          setStatus('failure');
        }
      )
  }

  const handleInvalidSubmission = () => {
    if (logComponent('StaffCheckIn')) { console.debug('StaffCheckIn handleInvalidSubmission'); }
    modalRef.current.scrollTop = 0;
    setFormIsInvalid(true);
  }

  const getRequiredFieldList = () => {
    if (logComponent('StaffCheckIn')) { console.debug('StaffCheckIn getRequiredFieldList'); }
    let requiredFields = ['observedMood', 'staffCheckHistoryReasonTypeTags'];

    if (organization.isStaffCheckHistoryStaffNoteRequired) requiredFields.push('staffNote');
    if (organization.isStaffCheckHistoryFamilyNoteRequired && ((user.canCorrespond || user.hasCorrespondenceViaGroupsOrOverride))) requiredFields.push('familyNote');

    return requiredFields;
  }

  const startModalExit = () => {
    if (logComponent('StaffCheckIn')) { console.debug('StaffCheckIn startModalExit'); }
    setModalShouldExit(true);
  }

  const cancelCheckin = () => {
    if (logComponent('StaffCheckIn')) { console.debug('StaffCheckIn cancelCheckin'); }
    const cancelledStaffCheckHistory = createCancelledStaffCheckHistory();
    let newCancellationAttempts = cancellationAttempts + 1;
    setStatus('pending');
    setCancellationAttempts(newCancellationAttempts);

    baseApi.CheckIn.SaveCancelled(cancelledStaffCheckHistory)
      .then(
        result => {
          setStatus('success');
          startModalExit();
        }, error => {
          // TODO: Figure out what to do on error; prevent them from closing modal?
          if (newCancellationAttempts < 3) {
            cancelCheckin();
          } else {
            startModalExit();
          }
        }
      )
  }

  const createCancelledStaffCheckHistory = () => {
    if (logComponent('StaffCheckIn')) { console.debug('StaffCheckIn createCancelledStaffCheckHistory'); }

    let staffCheckInEntries = Object.entries(staffCheckIn);
    let staffCheckIn = {};

    staffCheckInEntries.forEach(entry => {
      staffCheckIn[entry[0]] = entry[1] ? entry[1].value : null;
    })

    // Move StaffNote and FamilyNote into StaffCheckHistoryNote object array
    staffCheckIn.cancelledStaffCheckHistoryNotes = [];

    if (staffCheckIn.staffNote) {
      staffCheckIn.cancelledStaffCheckHistoryNotes.push({ body: staffCheckIn.staffNote, type: 'Staff'});
    }

    if (staffCheckIn.familyNote) {
      staffCheckIn.cancelledStaffCheckHistoryNotes.push({ body: staffCheckIn.familyNote, type: 'Family'});
    }

    staffCheckIn.cancelledStaffCheckHistoryReasonTypeTags = staffCheckIn.staffCheckHistoryReasonTypeTags;

    // Delete original StaffNote and FamilyNote from StaffCheckIn object
    delete staffCheckIn.staffNote;
    delete staffCheckIn.familyNote;
    delete staffCheckIn.staffCheckHistoryReasonTypeTags;
    delete staffCheckIn.checkOutOn;

    // Add check out time, user id, monitored individual id, unit id
    staffCheckIn.cancelledOn = new Date();
    staffCheckIn.userId = user.userId;

    if (selectedMonitoredIndividual) {
      staffCheckIn.monitoredIndividualId = selectedMonitoredIndividual.monitoredIndividualId;
      staffCheckIn.unitId = selectedMonitoredIndividual.monitoredIndividualUnits[0].unit.unitId;  
    } else if (selectedUnit) {
      staffCheckIn.unitId = selectedUnit.unitId;        
    }

    return staffCheckIn;
  }

  const silentCancelCheckin = () => {
    if (logComponent('StaffCheckIn')) { console.debug('StaffCheckIn silentCancelCheckin'); }
    const cancelledStaffCheckHistory = createCancelledStaffCheckHistory();
    
    baseApi.CheckIn.SaveCancelled(cancelledStaffCheckHistory)
  }

  return (
    <Modal isVisible={true} shouldExit={modalShouldExit} hide={close} ref={setModalRef} remainOnBackdropClick={true}>
      <div className="Staff-Check-In">
        <h4 className="Staff-Check-In__heading">
          <span>Checking in</span>
          <Timer startTime={startTime} />
        </h4>
        <p className={'Staff-Check-In__page-error' + (formIsInvalid ? ' visible' : '')}>There were some errors. Please double check the information and resubmit.</p>
        <p className={"Staff-Check-In__page-error" + (status === 'failure' ? ' visible' : '')} >Sorry, something went wrong.  Changes were not saved.  Please try again.</p>

        <div className="Staff-Check-In__monitoredindividual-info">
          <div className="Staff-Check-In__monitoredindividual-image">
            <MonitoredIndividualImage monitoredIndividual={selectedMonitoredIndividual} />
          </div>
          <div className="Staff-Check-In__monitoredindividual-text">
            {
              selectedMonitoredIndividual &&
              <p className="Staff-Check-In__monitoredindividual-name">{`${selectedMonitoredIndividual.legalFirstName} ${selectedMonitoredIndividual.lastName}`}</p>
            }
            <p className="Staff-Check-In__monitoredindividual-units">
              {
                selectedMonitoredIndividual ?
                  selectedMonitoredIndividual.monitoredIndividualUnits.map((individualUnit, index) =>
                    <span key={individualUnit.unit.unitId}>{(index > 0 ? ', ' : '') + individualUnit.unit.name}</span>
                  ) :
                  selectedUnit ?
                    <span>{selectedUnit.name}</span> :
                    null
              }
            </p>
            <p className="Staff-Check-In__facility-info">
              {organization.name}
            </p>
          </div>
        </div>
        <form className="Staff-Check-In__form">
          <SearchSelect
            label="Reason for visit"
            selectionMode="limit"
            itemKeyField="staffCheckReasonTypeId"
            items={organization.staffCheckReasonTypes}
            itemsLoading={false}
            itemsError={false}
            itemPluralTerm="reasons"
            searchFieldGroups={[
              ['name']
            ]}
            displayField="name"
            name="staffCheckHistoryReasonTypeTags"
            rules={{
              minItems: 1,
              required: true
            }}
            showError={formIsInvalid}
            errorMessage="Please select a reason for this visit."
            onValidate={validateField}
          />
          <MoodSelection
            onValidate={validateField}
            errorMessage="Please select a mood."
            showError={formIsInvalid}
            rules={{
              required: true
            }}
            name="observedMood" />
          <TextAreaInput
            label={'Internal notes' + (!organization.isStaffCheckHistoryStaffNoteRequired ? ' (Optional)' : '')}
            name="staffNote"
            rules={
              organization.isStaffCheckHistoryStaffNoteRequired ?
                { required: true, maxLength: 500 } :
                { maxLength: 500 }
            }
            errorMessage="Please provide a note for staff.  500 characters or fewer."
            showError={formIsInvalid}
            onValidate={validateField} />
          {
            (selectedMonitoredIndividual && (user.canCorrespond || user.hasCorrespondenceViaGroupsOrOverride)) &&
            <TextAreaInput
              label={'Family notes' + (!organization.isStaffCheckHistoryFamilyNoteRequired ? ' (Optional)' : '')}
              subLabel="These notes will be sent to the individual's family members."
              name="familyNote"
              rules={
                organization.isStaffCheckHistoryFamilyNoteRequired ?
                  { required: true, maxLength: 500 } :
                  { maxLength: 500 }
              }
              errorMessage="Please provide a note for the individual's family members. 500 characters or fewer."
              showError={formIsInvalid}
              onValidate={validateField} />
          }
          <ActionButtons
            ref={actionButtons}
            mainAction={submitCheckIn}
            mainActionText="Submit check in"
            cancel={cancelCheckin}
            cancelText="Cancel"
            currentStatus={status} />
        </form>
      </div>
    </Modal>

  );
}
